import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface AdminAuthState {
	isAdmin: boolean;
	isLoading: boolean;
	error: string | null;
}

/**
 * Custom hook for admin authentication and authorization
 * Redirects non-admin users and provides loading states
 */
export const useAdminAuth = (redirectPath: string = "/parent-dashboard") => {
	const { user, profile, loading } = useAuth();
	const navigate = useNavigate();
	const [authState, setAuthState] = useState<AdminAuthState>({
		isAdmin: false,
		isLoading: true,
		error: null,
	});

	useEffect(() => {
		// Checking admin status

		if (loading) {
			setAuthState((prev) => ({ ...prev, isLoading: true }));
			return;
		}

		if (!user || !profile) {
			// No user or profile, redirecting to login
			setAuthState({
				isAdmin: false,
				isLoading: false,
				error: "Authentication required",
			});
			navigate("/login");
			return;
		}

		if (profile.role !== "admin") {
			// User is not admin, redirecting
			setAuthState({
				isAdmin: false,
				isLoading: false,
				error: "Admin access required",
			});
			navigate(redirectPath);
			return;
		}

		// Admin access confirmed
		setAuthState({
			isAdmin: true,
			isLoading: false,
			error: null,
		});
	}, [user, profile, loading, navigate, redirectPath]);

	return authState;
};

/**
 * Hook for checking admin permissions for specific actions
 */
export const useAdminPermissions = () => {
	const { profile } = useAuth();

	const permissions = {
		// User management permissions
		canViewUsers: profile?.role === "admin",
		canEditUsers: profile?.role === "admin",
		canDeleteUsers: profile?.role === "admin",

		// Doula application permissions
		canViewApplications: profile?.role === "admin",
		canApproveApplications: profile?.role === "admin",
		canRejectApplications: profile?.role === "admin",
		canEditApplications: profile?.role === "admin",

		// Messaging permissions
		canViewAllMessages: profile?.role === "admin",
		canModerateMessages: profile?.role === "admin",

		// Platform settings permissions
		canViewSettings: profile?.role === "admin",
		canUpdateSettings: profile?.role === "admin",

		// FAQ management permissions
		canManageFaqs: profile?.role === "admin",

		// System administration
		canViewLogs: profile?.role === "admin",
		canManageSystem: profile?.role === "admin",
	};

	const hasPermission = (permission: keyof typeof permissions): boolean => {
		return permissions[permission] || false;
	};

	const requirePermission = (
		permission: keyof typeof permissions,
		errorMessage?: string,
	) => {
		if (!hasPermission(permission)) {
			throw new Error(errorMessage || `Permission required: ${permission}`);
		}
	};

	return {
		permissions,
		hasPermission,
		requirePermission,
		isAdmin: profile?.role === "admin",
	};
};

/**
 * Hook for admin-only API calls with automatic error handling
 */
export const useAdminAPI = () => {
	const { user } = useAuth();
	const { hasPermission } = useAdminPermissions();

	const makeAdminRequest = async (
		url: string,
		options: RequestInit = {},
		requiredPermission?: keyof ReturnType<
			typeof useAdminPermissions
		>["permissions"],
	) => {
		// Check permission if specified
		if (requiredPermission && !hasPermission(requiredPermission)) {
			throw new Error(
				`Access denied: ${requiredPermission} permission required`,
			);
		}

		if (!user) {
			throw new Error("Authentication required");
		}

		// Get the session token
		const {
			data: { session },
		} = await user.getSession();
		if (!session) {
			throw new Error("No valid session");
		}

		const requestOptions: RequestInit = {
			...options,
			headers: {
				Authorization: `Bearer ${session.access_token}`,
				"Content-Type": "application/json",
				...options.headers,
			},
		};

		const response = await fetch(url, requestOptions);

		if (!response.ok) {
			if (response.status === 401) {
				throw new Error("Authentication failed");
			}
			if (response.status === 403) {
				throw new Error("Admin access required");
			}
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.error || `Request failed: ${response.status}`);
		}

		return response.json();
	};

	return { makeAdminRequest };
};
